import {
  POLICY_FILTER_FIELDS,
  POLICY_GROUP_BY,
  POLICY_OWNED_STATES,
  POLICY_SORT_PAGINATION_FIELDS,
  POLICY_TYPES,
} from 'src/services/consts/policy.const'
import { ROUTES } from 'src/router/const'
import { TABLE_COL_NAMES } from 'src/services/consts/table-cell.const'
import { DIRECTORIES, TICKET_STATUS } from 'src/services/consts/ticket.const'
import { TICKET_CUSTOM_IDS } from 'src/services/consts/ticket-creation.const'
import { isTicketUnderReview } from 'src/services/helpers/ticket.helper'
import { formatWebsiteUrl, sortItems } from 'src/services/app/util.service'

const COMPANY_WIDE = 'all'

export function filterPolicyRows(rows, filterBy) {
  if (!Array.isArray(rows)) return []

  const searchTerm = filterBy?.search ? filterBy.search.toLowerCase() : null

  return rows.filter((row) => {
    return (
      !searchTerm ||
      row.companyName?.toLowerCase().includes(searchTerm) ||
      row.productName?.toLowerCase().includes(searchTerm)
    )
  })
}

export function getDirectoryCounts(rowsByType) {
  const allowList = rowsByType?.[POLICY_TYPES.ALLOW_LIST]
  const blockList = rowsByType?.[POLICY_TYPES.BLOCK_LIST]
  return {
    [POLICY_TYPES.ALLOW_LIST]: Array.isArray(allowList) ? allowList.length : 0,
    [POLICY_TYPES.BLOCK_LIST]: Array.isArray(blockList) ? blockList.length : 0,
  }
}

export function getDefaultFilter() {
  return {
    ...POLICY_FILTER_FIELDS,
  }
}

export function getDefaultSort() {
  return {
    ...POLICY_SORT_PAGINATION_FIELDS,
  }
}

export function getPolicyGroupBy(sortBy) {
  return sortBy === POLICY_GROUP_BY.COMPANY ? POLICY_GROUP_BY.COMPANY : POLICY_GROUP_BY.PRODUCT
}

export function sortPolicyRows(rows, sortBy, descending) {
  const primary = getPolicyGroupBy(sortBy)
  const secondary =
    primary === POLICY_GROUP_BY.COMPANY
      ? TABLE_COL_NAMES.PRODUCT_NAME
      : TABLE_COL_NAMES.COMPANY_NAME

  return sortItems(sortItems(rows, secondary, false), primary, descending)
}

export function refactorPolicies(data = {}) {
  const payload = data && typeof data === 'object' ? data : {}
  return {
    [POLICY_TYPES.ALLOW_LIST]: refactorListMap(payload.white_list),
    [POLICY_TYPES.BLOCK_LIST]: refactorListMap(payload.black_list),
  }
}

export function buildPolicyIndex(policies) {
  return {
    [POLICY_TYPES.ALLOW_LIST]: buildListIndex(policies?.[POLICY_TYPES.ALLOW_LIST]),
    [POLICY_TYPES.BLOCK_LIST]: buildListIndex(policies?.[POLICY_TYPES.BLOCK_LIST]),
  }
}

export function buildOwnedProductIndex({ tickets, approvals }) {
  const approvalsByProduct = new Map()
  const requestsByProduct = new Map()

  ;(approvals || []).forEach((approval) => {
    if (!isListedProduct(approval.companyId, approval.productId)) return
    const key = productKey(approval.companyId, approval.productId)

    if (approval.active) {
      setOwnedRecord(approvalsByProduct, key, {
        approvalId: approval.approvalId,
        ticketId: approval.ticketId || null,
        dateCreated: approval.dateCreated,
      })
      return
    }

    if (approval.ticketId) {
      setOwnedRecord(requestsByProduct, key, {
        approvalId: null,
        ticketId: approval.ticketId,
        dateCreated: approval.dateCreated,
      })
    }
  })
  ;(tickets || []).forEach((ticket) => {
    if (!isListedProduct(ticket.companyId, ticket.productId)) return
    const key = productKey(ticket.companyId, ticket.productId)

    if (ticket.ticketStatus === TICKET_STATUS.CLOSED && ticket.approved === true) {
      if (approvalsByProduct.has(key) || requestsByProduct.has(key)) return
      setOwnedRecord(approvalsByProduct, key, {
        approvalId: null,
        ticketId: ticket.ticketId,
        dateCreated: ticket.dateCreated,
      })
      return
    }

    const isInFlight =
      ticket.ticketStatus === TICKET_STATUS.OPENED || isTicketUnderReview(ticket.ticketStatus)
    if (isInFlight) {
      setOwnedRecord(requestsByProduct, key, {
        approvalId: null,
        ticketId: ticket.ticketId,
        dateCreated: ticket.dateCreated,
      })
    }
  })

  return { approvalsByProduct, requestsByProduct }
}

export function getOwnedProductRecord({ index, companyId, productId }) {
  if (!companyId || !productId || !index) return null

  const record =
    index.approvalsByProduct.get(productKey(companyId, productId)) ||
    index.requestsByProduct.get(productKey(companyId, productId))

  if (!record) return null

  const ownedState = record.approvalId
    ? POLICY_OWNED_STATES.APPROVAL
    : record.ticketId
      ? POLICY_OWNED_STATES.REQUEST
      : null

  if (!ownedState) return null

  return {
    ownedState,
    approvalId: record.approvalId || null,
    ticketId: record.ticketId || null,
  }
}

function hasActiveApprovalAtCompany(approvals, companyId) {
  if (!companyId || !Array.isArray(approvals)) return false

  return approvals.some(
    (approval) => approval.active && isSameCompanyId(approval.companyId, companyId)
  )
}

function hasDeniedTicketAtCompany(tickets, companyId) {
  if (!companyId || !Array.isArray(tickets)) return false

  return tickets.some(
    (ticket) =>
      isSameCompanyId(ticket.companyId, companyId) &&
      ticket.ticketStatus === TICKET_STATUS.CLOSED &&
      ticket.approved === false
  )
}

function hasDraftTicketAtCompany(tickets, companyId) {
  if (!companyId || !Array.isArray(tickets)) return false

  return tickets.some(
    (ticket) =>
      isSameCompanyId(ticket.companyId, companyId) && ticket.ticketStatus === TICKET_STATUS.OPENED
  )
}

export function getOwnedApprovalTo({ row, routeQuery }) {
  if (row?.ownedState !== POLICY_OWNED_STATES.APPROVAL || !row.approvalId) return null

  const query = { ...routeQuery, approvalId: row.approvalId }
  delete query.ticketId
  return { query }
}

export function getOwnedRequestTo({ row, routeQuery }) {
  if (row?.ownedState !== POLICY_OWNED_STATES.REQUEST || !row.ticketId) return null

  const query = { ...routeQuery, ticketId: row.ticketId }
  delete query.approvalId
  return { query }
}

export function canRequestPolicyProduct(row) {
  return !!row?.productId && !row.ownedState
}

export function getCompanyApprovalsTo({ approvals, companyId, companyName, listType }) {
  if (!companyId || !companyName || listType !== POLICY_TYPES.ALLOW_LIST) return null
  if (!hasActiveApprovalAtCompany(approvals, companyId)) return null

  return {
    name: ROUTES.APPROVALS,
    query: { search: companyName },
  }
}

export function getCompanyRequestsTo({ tickets, companyId, companyName, listType }) {
  if (!companyId || !companyName || listType !== POLICY_TYPES.BLOCK_LIST) return null

  if (hasDeniedTicketAtCompany(tickets, companyId)) {
    return {
      name: ROUTES.TICKETS,
      query: { search: companyName, status: DIRECTORIES.denied },
    }
  }

  if (hasDraftTicketAtCompany(tickets, companyId)) {
    return {
      name: ROUTES.TICKETS,
      query: { search: companyName, status: DIRECTORIES.drafts },
    }
  }

  return null
}

export function canRequestCompanyProduct({ row, companyApprovalsTo, companyRequestsTo }) {
  return !!row?.isCompanyWide && !row.productId && !companyApprovalsTo && !companyRequestsTo
}

export function getMembership({ policies, companyId, productId, showAllowList, showBlockList }) {
  if (
    !companyId ||
    TICKET_CUSTOM_IDS.includes(companyId) ||
    TICKET_CUSTOM_IDS.includes(productId)
  ) {
    return null
  }

  const type = matchesList(policies?.[POLICY_TYPES.BLOCK_LIST], companyId, productId)
    ? POLICY_TYPES.BLOCK_LIST
    : matchesList(policies?.[POLICY_TYPES.ALLOW_LIST], companyId, productId)
      ? POLICY_TYPES.ALLOW_LIST
      : null

  if (type === POLICY_TYPES.ALLOW_LIST && !showAllowList) return null
  if (type === POLICY_TYPES.BLOCK_LIST && !showBlockList) return null
  return type
}

export function enrichPolicyEntries({ list, directory }) {
  if (!list || typeof list !== 'object') return []

  const entries = Object.entries(list).map(([companyId, ids]) => {
    const productIds = Array.isArray(ids) ? ids : []
    const isCompanyWide = productIds.includes(COMPANY_WIDE)

    return {
      companyId,
      companyName: directory.getCompanyName({ companyId }),
      website: formatWebsiteUrl(directory.getWebsite(companyId)),
      isCompanyWide,
      products: isCompanyWide
        ? []
        : productIds.map((productId) => ({
            productId,
            productName: directory.getProductName({ companyId, productId }),
          })),
    }
  })

  return entries.sort((a, b) =>
    (a.companyName || '').toLowerCase().localeCompare((b.companyName || '').toLowerCase())
  )
}

export function flattenPolicyRows(entries) {
  if (!Array.isArray(entries)) return []

  return entries.flatMap((entry) => {
    if (entry.isCompanyWide) {
      return [
        {
          rowKey: entry.companyId,
          companyId: entry.companyId,
          companyName: entry.companyName,
          website: entry.website,
          productId: null,
          productName: 'All products',
          isCompanyWide: true,
        },
      ]
    }

    return (entry.products || []).map((product) => ({
      rowKey: `${entry.companyId}:${product.productId}`,
      companyId: entry.companyId,
      companyName: entry.companyName,
      website: entry.website,
      productId: product.productId,
      productName: product.productName,
      isCompanyWide: false,
    }))
  })
}

function productKey(companyId, productId) {
  return `${String(companyId)}:${String(productId)}`
}

function isSameCompanyId(left, right) {
  return !!left && !!right && String(left) === String(right)
}

function setOwnedRecord(map, key, record) {
  const current = map.get(key)
  if (!current) {
    map.set(key, record)
    return
  }

  const currentTime = current.dateCreated ? new Date(current.dateCreated).getTime() : 0
  const nextTime = record.dateCreated ? new Date(record.dateCreated).getTime() : 0
  if (nextTime >= currentTime) {
    map.set(key, record)
  }
}

function isListedProduct(companyId, productId) {
  return (
    !!companyId &&
    !!productId &&
    !TICKET_CUSTOM_IDS.includes(companyId) &&
    !TICKET_CUSTOM_IDS.includes(productId)
  )
}

function refactorListMap(list) {
  if (!list || typeof list !== 'object' || Array.isArray(list)) return {}

  return Object.entries(list).reduce((acc, [companyId, value]) => {
    const products = Array.isArray(value) ? value : []
    if (products.length) {
      acc[companyId] = products
    }
    return acc
  }, {})
}

function buildListIndex(list) {
  const companyWide = new Set()
  const products = new Set()

  if (!list || typeof list !== 'object') {
    return { companyWide, products }
  }

  Object.entries(list).forEach(([companyId, ids]) => {
    if (!Array.isArray(ids)) return
    if (ids.includes(COMPANY_WIDE)) {
      companyWide.add(companyId)
      return
    }
    ids.forEach((productId) => {
      products.add(`${companyId}:${productId}`)
    })
  })

  return { companyWide, products }
}

function matchesList(listIndex, companyId, productId) {
  if (!listIndex) return false

  // Company rows match only a company-wide listing. Tagging a company because
  // some of its products are listed would be inaccurate — accuracy over visibility.
  if (!productId) {
    return listIndex.companyWide.has(companyId)
  }

  return listIndex.companyWide.has(companyId) || listIndex.products.has(`${companyId}:${productId}`)
}
